import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatPrice } from "../../lib/api";
import { Search } from "lucide-react";

const STATUS_COLORS = {
  paid: "bg-blue-500/20 text-blue-200 border-blue-500/40",
  packed: "bg-yellow-500/20 text-yellow-200 border-yellow-500/40",
  shipped: "bg-purple-500/20 text-purple-200 border-purple-500/40",
  out_for_delivery: "bg-pink-500/20 text-pink-200 border-pink-500/40",
  delivered: "bg-emerald-500/20 text-emerald-200 border-emerald-500/40",
  cancelled: "bg-red-500/20 text-red-200 border-red-500/40",
};

const STATUSES = ["all", "paid", "packed", "shipped", "out_for_delivery", "delivered", "cancelled"];

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (status && status !== "all") params.set("status", status);
    if (q) params.set("q", q);
    api.get(`/admin/orders?${params.toString()}`)
      .then(({ data }) => setOrders(data))
      .catch(() => setOrders([]))
      .finally(() => setLoading(false));
  }, [status, q]);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display font-extrabold text-4xl tracking-tighter text-white">Orders</h1>
          <p className="mt-1 text-sm text-white/50">{orders.length} {status !== "all" && `(${status.replace(/_/g, ' ')})`}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center bg-[#110E1D] border border-white/10 rounded-md px-3 h-10 focus-within:border-[#B026FF]/60">
            <Search size={14} className="text-white/40" />
            <input
              value={q} onChange={(e) => setQ(e.target.value)}
              data-testid="admin-orders-search"
              placeholder="Search by order # / email / name"
              className="bg-transparent outline-none text-sm text-white ml-2 w-72"
            />
          </div>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        {STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            data-testid={`admin-status-filter-${s}`}
            className={`text-[11px] tracking-[0.18em] uppercase font-bold px-3 py-1.5 rounded-full border transition ${
              status === s
                ? "bg-[#B026FF]/20 text-white border-[#B026FF]/50"
                : "bg-transparent text-white/60 border-white/10 hover:border-white/30"
            }`}
          >
            {s.replace(/_/g, ' ')}
          </button>
        ))}
      </div>

      <div className="mt-8 card-elev rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0A0812] text-[10px] tracking-[0.22em] uppercase text-white/40 font-bold">
            <tr>
              <th className="text-left px-5 py-3">Order</th>
              <th className="text-left px-5 py-3">Customer</th>
              <th className="text-left px-5 py-3">Items</th>
              <th className="text-left px-5 py-3">Status</th>
              <th className="text-right px-5 py-3">Total</th>
              <th className="text-left px-5 py-3">Placed</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="px-5 py-10 text-center text-white/50">Loading…</td></tr>}
            {!loading && orders.map((o) => (
              <tr
                key={o.id}
                onClick={() => nav(`/admin/orders/${o.id}`)}
                data-testid={`admin-order-${o.order_number}`}
                className="border-t border-white/5 hover:bg-white/[0.02] cursor-pointer"
              >
                <td className="px-5 py-4">
                  <div className="font-mono text-white">{o.order_number}</div>
                  {o.tracking_number && <div className="text-[10px] text-[#B026FF] font-mono mt-0.5">{o.tracking_number}</div>}
                </td>
                <td className="px-5 py-4">
                  <div className="text-white">{o.name}</div>
                  <div className="text-xs text-white/50">{o.email}</div>
                </td>
                <td className="px-5 py-4 text-white/70">{o.items.length}</td>
                <td className="px-5 py-4">
                  <span className={`text-[10px] tracking-[0.18em] uppercase font-bold px-2 py-1 rounded border ${STATUS_COLORS[o.status] || ""}`}>
                    {o.status.replace(/_/g, ' ')}
                  </span>
                </td>
                <td className="px-5 py-4 text-right font-mono text-white">{formatPrice(o.total_cents)}</td>
                <td className="px-5 py-4 text-white/60 text-xs">{new Date(o.created_at).toLocaleString()}</td>
              </tr>
            ))}
            {!loading && orders.length === 0 && (
              <tr><td colSpan={6} className="px-5 py-10 text-center text-white/50">No orders found</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
